import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Link } from 'react-router-dom';

const ServiceTestimonials = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const testimonials = [
    {
      id: 1,
      service: "Custom Software Development",
      client: {
        name: "Sarah Johnson",
        position: "CTO",
        company: "TechFlow Solutions",
        avatar: "https://randomuser.me/api/portraits/women/32.jpg",
        companyLogo: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100&h=100&fit=crop"
      },
      rating: 5,
      testimonial: `Shutter Lifters transformed our legacy system into a modern, scalable platform that handles 10x our previous traffic. Their technical expertise and attention to detail exceeded our expectations.

The team's ability to understand complex business requirements and translate them into elegant technical solutions is remarkable. Our productivity increased by 40% after the implementation.`,
      project: {
        name: "Enterprise Resource Planning System",
        duration: "8 months",
        technology: "React, Node.js, PostgreSQL",
        result: "40% productivity increase"
      },
      color: "accent"
    },
    {
      id: 2,
      service: "Web Application Engineering",
      client: {
        name: "Michael Chen",
        position: "Founder & CEO",
        company: "E-Commerce Plus",
        avatar: "https://randomuser.me/api/portraits/men/45.jpg",
        companyLogo: "https://images.unsplash.com/photo-1572021335469-31706a17aaef?w=100&h=100&fit=crop"
      },
      rating: 5,
      testimonial: `The web application they built for us handles massive traffic spikes during sales events without any issues. The performance optimization and scalable architecture they implemented saved us thousands in server costs.

Their expertise in modern web technologies and user experience design resulted in a 300% increase in conversion rates. Absolutely phenomenal work!`,
      project: {
        name: "High-Traffic E-commerce Platform",
        duration: "6 months",
        technology: "Next.js, GraphQL, AWS",
        result: "300% conversion increase"
      },
      color: "success"
    },
    {
      id: 3,
      service: "Mobile Solutions",
      client: {
        name: "Dr. Emily Rodriguez",
        position: "Medical Director",
        company: "HealthTech Innovations",
        avatar: "https://randomuser.me/api/portraits/women/28.jpg",
        companyLogo: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=100&h=100&fit=crop"
      },
      rating: 5,
      testimonial: `They developed a HIPAA-compliant mobile app that revolutionized how we deliver healthcare services. The app's intuitive design and robust security features have been praised by both patients and medical staff.

The offline functionality and real-time synchronization they implemented ensures our medical professionals can access critical patient data anywhere. Outstanding technical execution!`,
      project: {
        name: "HIPAA-Compliant Healthcare App",
        duration: "10 months",
        technology: "React Native, Firebase, AWS",
        result: "50K+ active users"
      },
      color: "warning"
    },
    {
      id: 4,
      service: "UI/UX Design",
      client: {
        name: "David Park",
        position: "Product Manager",
        company: "FinTech Startup",
        avatar: "https://randomuser.me/api/portraits/men/35.jpg",
        companyLogo: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=100&h=100&fit=crop"
      },
      rating: 5,
      testimonial: `Their UI/UX design transformed our complex financial dashboard into an intuitive, user-friendly interface. The user research and testing they conducted resulted in an 85% user satisfaction score.

The design system they created not only looks beautiful but also significantly improved our development velocity. Our users now spend 60% more time engaging with our platform.`,
      project: {
        name: "Financial Dashboard Redesign",
        duration: "4 months",
        technology: "Figma, React, D3.js",
        result: "85% user satisfaction"
      },
      color: "purple-500"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials?.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [testimonials?.length]);

  const currentTestimonial = testimonials?.[activeTestimonial];

  return (
    <section className="py-20 bg-gradient-to-b from-background to-primary/5">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="Star" size={16} />
            <span>Client Success Stories</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            What Our Clients
            <span className="block bg-gradient-to-r from-accent to-success bg-clip-text text-transparent">
              Say About Us
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Real testimonials from real clients who have experienced the transformative 
            power of our software development and design services.
          </p>
        </motion.div>

        {/* Main Testimonial Display */}
        <motion.div
          key={activeTestimonial}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-6xl mx-auto"
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Testimonial Content */}
            <div className="space-y-8">
              {/* Service Badge */}
              <div className={`inline-flex items-center space-x-2 bg-${currentTestimonial?.color}/10 text-${currentTestimonial?.color} px-4 py-2 rounded-full text-sm font-medium`}>
                <Icon name="Briefcase" size={16} />
                <span>{currentTestimonial?.service}</span>
              </div>

              {/* Rating */}
              <div className="flex items-center space-x-2">
                {[...Array(currentTestimonial?.rating)]?.map((_, index) => (
                  <Icon key={index} name="Star" size={20} className="text-warning fill-current" />
                ))}
                <span className="text-sm text-muted-foreground ml-2">
                  {currentTestimonial?.rating}.0 out of 5
                </span>
              </div>

              {/* Testimonial Text */}
              <blockquote className="text-lg lg:text-xl text-foreground leading-relaxed whitespace-pre-line">
                "{currentTestimonial?.testimonial}"
              </blockquote>

              {/* Client Info */}
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Image
                    src={currentTestimonial?.client?.avatar}
                    alt={currentTestimonial?.client?.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-success rounded-full border-2 border-background flex items-center justify-center">
                    <Icon name="Check" size={12} className="text-white" />
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-foreground">
                    {currentTestimonial?.client?.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {currentTestimonial?.client?.position}
                  </div>
                  <div className={`text-sm text-${currentTestimonial?.color} font-medium`}>
                    {currentTestimonial?.client?.company}
                  </div>
                </div>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-6">
              {/* Company Logo */}
              <div className="text-center">
                <div className="inline-block p-4 bg-muted rounded-2xl">
                  <Image
                    src={currentTestimonial?.client?.companyLogo}
                    alt={`${currentTestimonial?.client?.company} logo`}
                    className="w-20 h-20 object-cover rounded-xl"
                  />
                </div>
              </div>

              {/* Project Card */}
              <div className={`bg-gradient-to-br from-${currentTestimonial?.color}/10 to-${currentTestimonial?.color}/5 border border-${currentTestimonial?.color}/20 rounded-2xl p-6 shadow-lg`}>
                <h4 className="font-bold text-foreground mb-4 flex items-center space-x-2">
                  <Icon name="Folder" size={20} className={`text-${currentTestimonial?.color}`} />
                  <span>Project Details</span>
                </h4>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-muted-foreground">Project Name</div>
                    <div className="font-medium text-foreground">
                      {currentTestimonial?.project?.name}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Duration</div>
                      <div className="font-medium text-foreground">
                        {currentTestimonial?.project?.duration}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Result</div>
                      <div className={`font-medium text-${currentTestimonial?.color}`}>
                        {currentTestimonial?.project?.result}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Technologies Used</div>
                    <div className="flex flex-wrap gap-2">
                      {currentTestimonial?.project?.technology?.split(', ')?.map((tech, index) => (
                        <span
                          key={index}
                          className={`px-3 py-1 bg-${currentTestimonial?.color}/20 text-${currentTestimonial?.color} text-xs rounded-full border border-${currentTestimonial?.color}/30`}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Testimonial Navigation */}
        <div className="flex justify-center space-x-4 mt-12">
          {testimonials?.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveTestimonial(index)}
              className={`w-4 h-4 rounded-full transition-all duration-300 ${
                index === activeTestimonial
                  ? `bg-${testimonials?.[activeTestimonial]?.color} shadow-lg scale-125`
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
              }`}
              aria-label={`View testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-3xl font-bold text-accent">95%</div>
              <div className="text-sm text-muted-foreground">Client Satisfaction</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-success">40+</div>
              <div className="text-sm text-muted-foreground">Projects Delivered</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-warning">1+</div>
              <div className="text-sm text-muted-foreground">Years Experience</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-bold text-purple-500">24/7</div>
              <div className="text-sm text-muted-foreground">Support Available</div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-accent/10 via-success/10 to-warning/10 border border-accent/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Join Our Success Stories?
            </h3>
            <p className="text-muted-foreground mb-6">
              Let's discuss how we can help transform your business with our proven 
              software development and design expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                // className="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-medium shadow-accent hover:bg-accent/90 transition-colors flex items-center justify-center space-x-2"
              >
                {/* routing to contact */}
                <Link to = "/contact" className='bg-accent text-accent-foreground px-6 py-3 rounded-lg font-medium shadow-accent hover:bg-accent/90 transition-colors flex items-center justify-center space-x-2'>
                <Icon name="MessageCircle" size={20} />
                <span>Start Your Project</span>
                </Link>
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-accent/30 text-accent px-6 py-3 rounded-lg font-medium hover:bg-accent/10 transition-colors flex items-center justify-center space-x-2"
              >
                <Icon name="FileText" size={20} />
                <span>View All Case Studies</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceTestimonials;